import React, { useEffect, useState } from "react";
import axios from "axios";
import { PageHeader, StatCard } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { API } from "../context/AuthContext";
import { BUSINESS } from "../config/business";
import { CalculatorPanel } from "../components/billing/CalculatorPanel";
import { InvoiceList } from "../components/billing/InvoiceList";
import { Receipt, CheckCircle } from "@phosphor-icons/react";

const TOKEN_KEY = "bj_admin_token";
const tokenCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` } });

const BillingPage = () => {
  const [summary, setSummary] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, r, l] = await Promise.all([
        axios.get(`${API}/billing/summary`, tokenCfg()),
        axios.get(`${API}/billing/reconciliation`, tokenCfg()).catch((e) => ({ data: { error: e?.response?.data?.detail || "reconciliation failed" } })),
        axios.get(`${API}/billing/invoices`, tokenCfg()),
      ]);
      setSummary(s.data);
      setReconciliation(r.data);
      setInvoices(l.data.invoices || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (loading) return <Loading label="Loading billing & invoices" />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;

  return (
    <div data-testid="billing-page">
      <PageHeader
        eyebrow="DASHBOARD / BILLING & INVOICES"
        title="Billing & Invoices"
        subtitle={`${invoices.length} invoices • Processed via PaymentCloud + Authorize.net • ${BUSINESS.tagline}`}
        right={
          reconciliation?.diverges_by_cents === 0 ? (
            <div data-testid="reconciliation-ok" className="flex items-center gap-2 border border-[#14281E]/30 text-[#14281E] px-3 py-2">
              <CheckCircle size={16} weight="fill" />
              <span className="font-heading text-xs tracking-widest uppercase">Reconciliation Green</span>
            </div>
          ) : (
            <div data-testid="reconciliation-fail" className="border border-[#5C1A24]/40 text-[#5C1A24] px-3 py-2 text-xs">
              Reconciliation FAIL: {reconciliation?.error || `${reconciliation?.diverges_by_cents}c divergence`}
            </div>
          )
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <StatCard testid="stat-total-collected" label="Collected"    value={(summary.total_collected_cents || 0) / 100} prefix="$" tone="green" />
        <StatCard testid="stat-outstanding"     label="Outstanding"  value={(summary.outstanding_cents || 0) / 100}     prefix="$" tone="amber" />
        <StatCard testid="stat-active-clients"  label="Active"       value={summary.active_clients || 0}                            tone="white" />
        <StatCard testid="stat-total-invoices"  label="Invoices"     value={summary.total_invoices || 0}                            tone="white" />
      </div>

      <CalculatorPanel onCreated={refresh} />

      <div className="mt-8 mb-3 flex items-center gap-2">
        <Receipt size={18} className="text-[#6E5022]" weight="bold" />
        <h3 className="font-heading text-[#2A1C15] text-xl">All Invoices</h3>
      </div>
      <InvoiceList invoices={invoices} onRefresh={refresh} />
    </div>
  );
};

export default BillingPage;
